import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { HttpService } from "src/app/shared/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { APIEndpoints } from "src/config/apiEndpoint.config";
import { ICommandAddress } from "../contracts/commandAddress.contract";
import { ICommandAttempt } from "../contracts/commandAttempt.contract";
import { ICommandDetails } from "../contracts/commandDetails.contract";
import { ICommandInfo } from "../contracts/commandInfo.contract";
import { ICommandStep } from "../contracts/commandStep.contract";
import { CommandAddress } from "../models/commandAddress.model";
import { CommandAttempt } from "../models/commandAttempt.model";
import { CommandDetails } from "../models/commandDetails.model";
import { CommandInfo } from "../models/commandInfo.model";
import { CommandLog } from "../models/commandLog.model";
import { CommandStep } from "../models/commandStep.model";

@Injectable({
  providedIn: "root",
})
export class CommandService {
  constructor(
    private httpService: HttpService,
    private loaderService: LoaderService
  ) {}

  postCommands(data: any): Observable<CommandInfo[]> {
    return this.httpService
      .post("/CommandDescriptor/Filter/", data, true)
      .pipe(
        map((res: any[]) => {
          return res.map((r) => {
            return { ...r } as ICommandInfo;
          });
        })
      )
      .pipe(map((res) => this.mapToCommandInfo(res)));
  }

  retryCommand(commandId: string) {
    return this.httpService.post(
      APIEndpoints.retryCommand,
      { commandId },
      true
    );
  }

  restartCommand(commandId: string, skipPrevalidation: boolean) {
    return this.httpService.post(
      APIEndpoints.restartCommand,
      { commandId, skipPrevalidation },
      true
    );
  }

  abandonCommand(commandId: string) {
    return this.httpService
      .post("/CommandHandlingAttempts/AbandonCommand", { Id: commandId })
      .pipe(
        map((res: any[]) => {
          return res.map((r) => {
            return { ...r } as ICommandInfo;
          });
        })
      );
  }

  getCommandDetails(commandId: string) {
    return this.httpService
      .get<ICommandDetails>(APIEndpoints.commandDetails(commandId))
      .pipe(map((res) => this.mapToCommandDetails(res)));
  }

  getLogsForCommand(commandId: string): Observable<CommandLog[]> {
    return this.httpService.get<CommandLog[]>(
      APIEndpoints.commandLogs(commandId)
    );
  }

  private mapToCommandInfo(commands: ICommandInfo[]): CommandInfo[] {
    return commands.map((c) => {
      const lastAttempt = this.mapToCommandAttempt(c.lastAttempt);

      const type = c.payload.substr(
        c.payload.indexOf('$type":'),
        c.payload.indexOf(",") - 2
      );
      const commandTitle = type.substring(type.lastIndexOf(".") + 1);

      const commandAddress = this.mapToCommandAddress(c.address);

      return new CommandInfo({
        id: c.id,
        payload: c.payload,
        collapsed: true,
        state: c.commandState,
        title: commandTitle,
        city: c.city,
        fiberhood: c.fiberhood,
        block: c.block,
        commandAddress: commandAddress,
        lastAttempt: lastAttempt,
        attempts: c.attempts,
        executedOn: new Date(c.executedOn.toString() + "Z"),
      });
    });
  }

  private mapToCommandDetails(commandDetails: ICommandDetails): CommandDetails {
    const attempts = commandDetails.attempts
      .map((a) => this.mapToCommandAttempt(a))
      .sort(
        (a, b) =>
          new Date(b.StartDate).valueOf() - new Date(a.StartDate).valueOf()
      );

    const steps = commandDetails.steps
      .map((s) => this.mapToCommandStep(s))
      .sort(
        (a, b) =>
          new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime()
      );

    const type = commandDetails.payload?.substr(
      commandDetails.payload.indexOf('$type":'),
      commandDetails.payload.indexOf(",") - 2
    );
    const commandTitle = type?.substring(type.lastIndexOf(".") + 1);

    return new CommandDetails({
      id: commandDetails.id,
      payload: commandDetails.payload,
      executedOn: new Date(commandDetails.executedOn.toString() + "Z"),
      title: commandTitle,
      attempts: attempts,
      steps: steps,
      commandState: commandDetails.commandState,
    });
  }

  private mapToCommandAttempt(commandAttempt: ICommandAttempt): CommandAttempt {
    const error = commandAttempt.error?.substr(
      commandAttempt.error.indexOf("Main Exception:") + 15,
      commandAttempt.error.indexOf("---")
    );

    return new CommandAttempt({
      id: commandAttempt?.id,
      error: error ? error : commandAttempt?.error,
      fullError: commandAttempt?.error,
      source: commandAttempt?.source,
      startDate: new Date(commandAttempt?.startDate + "Z"),
      state: commandAttempt?.state,
    });
  }

  private mapToCommandAddress(commandAddress: ICommandAddress): CommandAddress {
    return new CommandAddress({
      id: commandAddress?.id,
      streetName: commandAddress?.streetName,
      number: commandAddress?.number,
      suffix: commandAddress?.suffix,
      lomKey: commandAddress?.lomKey,
      buildingLomKey: commandAddress?.buildingLomKey,
    });
  }

  private mapToCommandStep(commandStep: ICommandStep): CommandStep {
    return new CommandStep({
      id: commandStep.id,
      state: commandStep.state,
      step: commandStep.step,
      timestamp: commandStep.timestamp,
    });
  }
}
